import { useState } from "react";
import { CalendarClock, CheckCircle2, FlaskConical, Loader2, Send, XCircle } from "lucide-react";
import Dialogo from "../sms/Dialogo";
import { brl, waApi } from "./modelos";

/**
 * Passo 3: teste no próprio WhatsApp e disparo (com confirmação do sistema,
 * nada de alert do navegador). `pedido` = {template, idioma, params, url_dinamica,
 * corpo, segmentos, nome}; os comandos ({nome}, {empresa}, {link}) o servidor preenche.
 */
// "2026-09-29 10:00" → "29/09 às 10:00"
const quandoBr = (ag) => (ag ? `${ag.slice(8, 10)}/${ag.slice(5, 7)} às ${ag.slice(11, 16)}` : "");

export default function EnviarCampanha({ pedido, total, custo, faltaSaldo, pronto, onConcluida }) {
  const [telefone, setTelefone] = useState("");
  const [testando, setTestando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [dialogo, setDialogo] = useState(null);

  const testar = async () => {
    setAviso(null);
    if (telefone.replace(/\D/g, "").length < 10) { setAviso({ tipo: "erro", txt: "Digite o seu WhatsApp com DDD." }); return; }
    setTestando(true);
    try {
      const r = await waApi.teste({ telefone, template: pedido.template, idioma: pedido.idioma,
        params: pedido.params, url_dinamica: pedido.url_dinamica });
      setAviso(r?.success ? { tipo: "ok", txt: "Teste enviado! Olhe o seu WhatsApp." } : { tipo: "erro", txt: r?.erro || "O teste não saiu." });
    } catch {
      setAviso({ tipo: "erro", txt: "Sem conexão com o servidor." });
    } finally { setTestando(false); }
  };

  const disparar = async () => {
    setDialogo(null); setEnviando(true);
    try {
      const r = await waApi.disparar(pedido);
      setDialogo(r?.success ? { ok: r } : { erro: r?.erro || "O disparo não saiu." });
    } catch {
      setDialogo({ erro: "Sem conexão com o servidor." });
    } finally { setEnviando(false); }
  };

  const bloqueado = !pronto || !total || faltaSaldo || enviando;
  const agendada = pedido?.agendada_para;
  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-semibold flex items-center gap-2"><FlaskConical className="h-4 w-4 text-primary" /> Mandar um teste pro meu WhatsApp</label>
        <div className="mt-1.5 flex gap-2">
          <input value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="(34) 99999-9999" inputMode="tel"
            className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm" />
          <button type="button" onClick={testar} disabled={!pronto || testando}
            className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50">
            {testando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar teste
          </button>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">No teste, o nome é o do cadastro desse número (se tiver) e o link é de teste.</p>
        {aviso && <p className={`mt-1.5 text-sm ${aviso.tipo === "ok" ? "text-emerald-600" : "text-destructive"}`}>{aviso.txt}</p>}
      </div>

      <button type="button" onClick={() => setDialogo("confirmar")} disabled={bloqueado}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#00a884] px-5 py-3.5 text-base font-bold text-white shadow-sm hover:bg-[#008f72] disabled:opacity-50">
        {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : agendada ? <CalendarClock className="h-5 w-5" /> : <Send className="h-5 w-5" />}
        {agendada ? `Agendar para ${quandoBr(agendada)}` : "Disparar agora"} · {total.toLocaleString("pt-BR")} cliente{total === 1 ? "" : "s"} · {custo ? brl(custo) : "sem custo"}
      </button>
      {!pronto ? (
        <p className="text-center text-xs text-muted-foreground">Escolha um modelo e preencha os campos de texto fixo.</p>
      ) : faltaSaldo ? (
        <p className="text-center text-xs text-amber-600">O saldo da carteira não cobre esta campanha. Adicione saldo no painel do SMS (a carteira é a mesma).</p>
      ) : null}

      {dialogo === "confirmar" && (
        <Dialogo icone={agendada ? CalendarClock : Send} titulo={agendada ? "Agendar a campanha?" : "Disparar a campanha?"}
          onFechar={() => setDialogo(null)}
          acoes={[{ rotulo: "Voltar", onClick: () => setDialogo(null) },
            { rotulo: agendada ? "Agendar" : "Disparar agora", primario: true, onClick: disparar }]}>
          <p className="text-center">
            A mensagem vai para <b>{total.toLocaleString("pt-BR")} cliente{total === 1 ? "" : "s"}</b>{" "}
            {agendada ? <>em <b>{quandoBr(agendada)}</b></> : "agora"}.{" "}
            {custo ? <>Custo de <b>{brl(custo)}</b>, descontado da carteira; o que não sair volta pro saldo.</> : "Sem custo nesta loja."}
          </p>
        </Dialogo>
      )}
      {dialogo?.ok && (
        <Dialogo icone={CheckCircle2} titulo={dialogo.ok.agendada ? "Campanha agendada" : "Campanha enviada"}
          onFechar={() => onConcluida?.()}
          acoes={[{ rotulo: "Fechar", primario: true, onClick: () => onConcluida?.() }]}>
          {dialogo.ok.agendada ? (
            <p className="text-center">
              Sai em <b>{quandoBr(dialogo.ok.agendada_para)}</b> para {dialogo.ok.total_alvo} cliente{dialogo.ok.total_alvo === 1 ? "" : "s"}.
              Até lá dá pra cancelar no painel do WhatsApp.
            </p>
          ) : (
            <p className="text-center">
              {dialogo.ok.enviados} mensage{dialogo.ok.enviados === 1 ? "m saiu" : "ns saíram"}
              {dialogo.ok.falhas ? `, ${dialogo.ok.falhas} não${custo ? " (o valor delas voltou pro saldo)" : ""}` : ""}.
            </p>
          )}
        </Dialogo>
      )}
      {dialogo?.erro && (
        <Dialogo icone={XCircle} tom="erro" titulo="Não deu certo" onFechar={() => setDialogo(null)}
          acoes={[{ rotulo: "Entendi", primario: true, onClick: () => setDialogo(null) }]}>
          <p className="text-center">{dialogo.erro}</p>
        </Dialogo>
      )}
    </div>
  );
}
